import { CalyxClient } from '@platform/http/calyx-client';
import { partnerSupplier } from '@/utils/environment';
import { useQuery } from '@tanstack/react-query';

export interface ActiveLandingPage {
  body: string;
  callToActionLabel: string | null;
  callToActionUrl: string | null;
  endDateDatetime: string;
  /** Presigned download URL, not the stored S3 key. Null when the page has no hero image. */
  heroImageUrl: string | null;
  startDateDatetime: string;
  supplierUUID: string;
  title: string;
  uuid: string;
}

interface ActiveLandingPageDto {
  body: string;
  call_to_action_label?: string | null;
  call_to_action_url?: string | null;
  end_date_datetime: string;
  hero_image_url?: string | null;
  start_date_datetime: string;
  supplier_uuid: string;
  title: string;
  uuid: string;
}

interface ActiveLandingPagesDto {
  landing_pages?: ActiveLandingPageDto[] | null;
}

const transformLandingPage = (
  dto: ActiveLandingPageDto,
): ActiveLandingPage => ({
  body: dto.body,
  callToActionLabel: dto.call_to_action_label ?? null,
  callToActionUrl: dto.call_to_action_url ?? null,
  endDateDatetime: dto.end_date_datetime,
  heroImageUrl: dto.hero_image_url ?? null,
  startDateDatetime: dto.start_date_datetime,
  supplierUUID: dto.supplier_uuid,
  title: dto.title,
  uuid: dto.uuid,
});

export const buildActiveLandingPagesPath = (
  businessOrganizationUUID: string,
  supplierUUID: string,
  restaurantUUID: string,
) =>
  `/business-organizations/${encodeURIComponent(businessOrganizationUUID)}` +
  `/suppliers/${encodeURIComponent(supplierUUID)}` +
  `/restaurants/${encodeURIComponent(restaurantUUID)}/landing-pages`;

export interface UseActiveLandingPagesOptions {
  restaurantUUID: string;
  supplierUUID: string;
}

interface UseActiveLandingPagesResult {
  error: Error | null;
  landingPages: ActiveLandingPage[];
  loading: boolean;
}

/**
 * Loads the supplier's active landing pages the user hasn't seen yet for the
 * currently selected (supplier, restaurant) pair (from UserContext)
 *
 * Calyx will pull user info off of the JWT
 */
export const useActiveLandingPages = (
  options: UseActiveLandingPagesOptions,
): UseActiveLandingPagesResult => {
  const { restaurantUUID, supplierUUID } = options;
  const businessOrganizationUUID = partnerSupplier.businessOrganizationUUID;

  const { data, error, isLoading } = useQuery({
    enabled: Boolean(supplierUUID && restaurantUUID),
    queryFn: async ({ signal }) => {
      const response = await CalyxClient.get<ActiveLandingPagesDto>(
        buildActiveLandingPagesPath(
          businessOrganizationUUID,
          supplierUUID,
          restaurantUUID,
        ),
        { signal },
      );
      return {
        landingPages: (response.landing_pages ?? []).map(transformLandingPage),
      };
    },
    queryKey: [
      'active-landing-pages',
      businessOrganizationUUID,
      supplierUUID,
      restaurantUUID,
    ],
    // Purposely don't want to refetch, because initially loaded landing pages shouldn't be affected by user events right after
    // (only relevant if user stays on landing page screen for >5 minutes, the current refresh default)
    staleTime: Infinity,
  });

  return {
    error: error ?? null,
    landingPages: data?.landingPages ?? [],
    loading: isLoading,
  };
};
